/**
 * @module Measurement
 * @description Measurement associated functions
 */
import { PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import type {
  GeneratedMeasurement,
  GeneratedPlanAreaMeasurement,
  Measurement,
  MeasurementType,
  PlanAreaMeasurement,
  PlanAreaMeasurementShape,
} from "building-types/entities/measurementTypes";
import { defaultReportCategories, PlanarMeasurementType, Sign } from "building-types/entities/measurementTypes";
import { FloorAreaTakeoffType } from "building-types/rooms/takeoffTypes";
import objectMap from "common/utils/objectMap";
import uid from "common/utils/uid";
import type { Mat3 } from "geom/mat3";
import { polygonSubtract, polygonUnion } from "geom/polygon";
import { polyLine2Move, polyLine2ReflectOverYAxis, polyLine2ReverseCopy, polyLine2TransformCopy } from "geom/polyLine2";
import { polygonArea } from "geom/polyLine2Polygon";
import type { PolyLine2MultiPolygon } from "geom/polyLine2Types";

export function planAreaMeasurementShapeNew(
  shape: Partial<PlanAreaMeasurementShape> & Pick<PlanAreaMeasurementShape, "path">,
): PlanAreaMeasurementShape {
  return {
    name: "",
    ...shape,
  };
}

export function reportTypes(type: MeasurementType) {
  switch (type) {
    case PlanarMeasurementType.PLANAR_AREA: {
      return Object.keys(FloorAreaTakeoffType);
    }
  }
}

export function planAreaMeasurementNew(parentId: string, from?: Partial<PlanAreaMeasurement>): PlanAreaMeasurement {
  return {
    entityType: "MEASUREMENT",
    name: "New Area Measurement",
    sortIndex: 0,
    measurementType: PlanarMeasurementType.PLANAR_AREA,
    positioningMethod: PositioningMethod.GLOBAL,
    parentEdgeId: "0",
    reportCategories: {
      [FloorAreaTakeoffType.TOTAL_FLOOR_AREA]: true,
      ...defaultReportCategories,
    },
    shapes: {},
    sign: Sign.ADDITIVE,
    ...from,
    id: uid(),
    parentId,
  };
}

export function isPlanAreaMeasurement(measurement: Measurement): measurement is PlanAreaMeasurement {
  return measurement.measurementType === PlanarMeasurementType.PLANAR_AREA;
}

interface MirrorMeasurementParams {
  readonly measurement: Measurement;
  readonly mirror: boolean;
  readonly mirrorTranslationX: number;
}

export function mirrorMeasurement({ measurement, mirror, mirrorTranslationX }: MirrorMeasurementParams) {
  if (!mirror) {
    return measurement;
  }
  switch (measurement.measurementType) {
    case PlanarMeasurementType.PLANAR_AREA: {
      return {
        ...measurement,
        shapes: objectMap(measurement.shapes, (shape) => ({
          ...shape,
          path: polyLine2Move(polyLine2ReflectOverYAxis(polyLine2ReverseCopy(shape.path)), mirrorTranslationX, 0),
        })),
      };
    }
    default: {
      return measurement;
    }
  }
}

export function measurementTransform(measurement: Measurement, mat: Mat3): Measurement {
  switch (measurement.measurementType) {
    case PlanarMeasurementType.PLANAR_AREA: {
      const newShapes = objectMap(measurement.shapes, (shape) => measuremenShapeTransform(shape, mat));

      return {
        ...measurement,
        shapes: newShapes,
      } as Measurement;
    }
    default: {
      return measurement;
    }
  }
}

export function measuremenShapeTransform(shape: PlanAreaMeasurementShape, mat: Mat3): PlanAreaMeasurementShape {
  return {
    ...shape,
    path: polyLine2TransformCopy(shape.path, mat),
  };
}

export function generateMeasurement(measurement: Measurement): GeneratedMeasurement {
  switch (measurement.measurementType) {
    case PlanarMeasurementType.PLANAR_AREA: {
      const { shapes, sign } = measurement;
      const areas: PolyLine2MultiPolygon = Object.values(shapes).map(({ path }) => [path]);
      const union = areas.length > 0 ? polygonUnion(areas) : [];

      let area = union.reduce((acc, poly) => acc + polygonArea(poly), 0) / 144;

      if (sign === Sign.SUBTRACTIVE) {
        area = -area;
      }

      return {
        ...measurement,
        area,
        union,
      };
    }
  }
}

/**
 * 1. unions all polygons and additive areas
 * 2. subtracts any subtractive areas from the union
 */
export function applyMeasurements(
  polygons: PolyLine2MultiPolygon,
  roomMeasurements: GeneratedPlanAreaMeasurement[],
): PolyLine2MultiPolygon {
  const subtractiveAreas: PolyLine2MultiPolygon = [];
  const additiveAreas: PolyLine2MultiPolygon = [];
  for (const { sign, union } of roomMeasurements) {
    if (sign === Sign.ADDITIVE) {
      additiveAreas.push(...union);
    } else {
      subtractiveAreas.push(...union);
    }
  }

  if (additiveAreas.length !== 0) {
    polygons = polygonUnion([...polygons, ...additiveAreas]);
  }

  if (subtractiveAreas.length === 0) {
    return polygons;
  }

  const diffed: PolyLine2MultiPolygon = [];
  for (const poly of polygons) {
    diffed.push(...polygonSubtract([poly, ...subtractiveAreas]));
  }

  return diffed;
}
